(() => {
  const cleanDisplayText = (value) =>
    String(value || '')
      .replace(/â€”|â€“|â€•/g, '—')
      .replace(/\s+/g, ' ')
      .trim();

  const normalizeMatch = (value) =>
    cleanDisplayText(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/ı/g, 'i')
      .replace(/\bvw\b/g, 'volkswagen')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const uniqueSorted = (values) =>
    [...new Set(values.map(cleanDisplayText).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b, 'tr', { numeric: true, sensitivity: 'base' })
    );

  const yearLabel = (vehicle) => {
    const start = cleanDisplayText(vehicle?.startYear);
    const end = cleanDisplayText(vehicle?.endYear);
    if (!start) return '';
    if (!end || start === end) return start;
    return `${start}-${end}`;
  };

  const splitModelParts = (raw) =>
    cleanDisplayText(raw)
      .split(/\s*[—–]\s*/u)
      .map((part) => part.trim())
      .filter(Boolean);

  const vehicleModelLabel = (vehicle) => {
    const parts = splitModelParts(vehicle?.model);
    const base = parts[0] || cleanDisplayText(vehicle?.model);
    const generation = cleanDisplayText(vehicle?.generation) || parts[1] || '';
    const bodyType = cleanDisplayText(vehicle?.bodyType) || parts[2] || '';

    let headline = base;
    if (generation) {
      const baseKey = normalizeMatch(base);
      const generationKey = normalizeMatch(generation);

      if (generationKey === baseKey) {
        headline = base;
      } else if (generationKey.startsWith(`${baseKey} `)) {
        // Kangoo + Kangoo II => "Kangoo II"; Megane + Megane III => "Megane III".
        headline = generation;
      } else if (baseKey.startsWith(`${generationKey} `)) {
        headline = base;
      } else {
        headline = `${base} ${generation}`.trim();
      }
    }

    if (bodyType && normalizeMatch(bodyType) !== normalizeMatch(headline)) {
      return `${headline} (${bodyType})`;
    }

    return headline;
  };

  const fillSelect = (select, placeholder, values) => {
    select.innerHTML = '';
    const first = document.createElement('option');
    first.value = '';
    first.textContent = placeholder;
    select.appendChild(first);

    values.forEach((item) => {
      const option = document.createElement('option');
      if (typeof item === 'object' && item !== null) {
        option.value = item.value;
        option.textContent = item.label || item.value;
      } else {
        option.value = item;
        option.textContent = item;
      }
      select.appendChild(option);
    });

    select.disabled = values.length === 0;
  };

  const modelOptionsFromVehicles = (brandVehicles) => {
    const representatives = new Map();

    brandVehicles.forEach((vehicle) => {
      const value = cleanDisplayText(vehicle?.model);
      if (value && !representatives.has(value)) representatives.set(value, vehicle);
    });

    const candidates = [...representatives.entries()].map(([value, vehicle]) => ({
      value,
      label: vehicleModelLabel(vehicle) || value,
    }));

    // Aynı görünen iki seçenek kalırsa kasa/nesil metnini sadece gerektiğinde ekle.
    const labelCounts = new Map();
    candidates.forEach((item) => {
      const key = normalizeMatch(item.label);
      labelCounts.set(key, (labelCounts.get(key) || 0) + 1);
    });

    return candidates
      .map((item) => {
        if ((labelCounts.get(normalizeMatch(item.label)) || 0) <= 1) return item;
        const parts = splitModelParts(item.value);
        const suffix = parts.slice(1).filter((part) => !normalizeMatch(item.label).includes(normalizeMatch(part))).join(' · ');
        return suffix ? { ...item, label: `${item.label} · ${suffix}` } : item;
      })
      .sort((a, b) => a.label.localeCompare(b.label, 'tr', { numeric: true, sensitivity: 'base' }));
  };

  const initialize = async (root) => {
    if (root.dataset.ypInitialized === 'true') return;
    root.dataset.ypInitialized = 'true';

    const oem = root.querySelector('[data-yp-oem]');
    const brand = root.querySelector('[data-yp-brand]');
    const model = root.querySelector('[data-yp-model]');
    const year = root.querySelector('[data-yp-year]');
    const engine = root.querySelector('[data-yp-engine]');
    const partGroup = root.querySelector('[data-yp-part-group]');
    const status = root.querySelector('[data-yp-status]');
    const submit = root.querySelector('[data-yp-submit]');
    let vehicles = [];
    let partGroups = [];
    let apiSource = '';

    const setStatus = (text, state = '') => {
      status.textContent = text;
      if (state) status.dataset.state = state;
      else delete status.dataset.state;
    };

    const currentBrandVehicles = () =>
      vehicles.filter((v) => normalizeMatch(v.brand) === normalizeMatch(brand.value));

    const currentModelVehicles = () =>
      currentBrandVehicles().filter((v) => String(v.model || '').trim() === model.value);

    const updateButton = () => {
      const hasOem = oem.value.trim().length > 0;
      const hasExactVehicle = Boolean(brand.value && model.value && year.value && engine.value);
      const hasPartGroup = Boolean(partGroup.value);

      submit.disabled = !(hasOem || (hasExactVehicle && hasPartGroup));
      submit.textContent = hasOem ? 'Koda göre ürünleri göster' : 'Uyumlu parçaları göster';
    };

    try {
      const separator = root.dataset.apiUrl.includes('?') ? '&' : '?';
      const response = await fetch(`${root.dataset.apiUrl}${separator}yp_selector=full&_=${Date.now()}`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        credentials: 'same-origin',
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const payload = await response.json();
      if (!payload.ok) throw new Error(payload.error || 'Araç verileri alınamadı.');

      // Engine exact-year satirlarinda endYear bilerek bos olabilir; startYear tek basina gecerlidir.
      vehicles = Array.isArray(payload.vehicles)
        ? payload.vehicles
            .filter((v) => v && v.brand && v.model && v.startYear)
            .map((v) => ({
              ...v,
              brand: cleanDisplayText(v.brand),
              model: cleanDisplayText(v.model),
              generation: cleanDisplayText(v.generation),
              bodyType: cleanDisplayText(v.bodyType),
              startYear: cleanDisplayText(v.startYear),
              endYear: cleanDisplayText(v.endYear),
              engine: cleanDisplayText(v.engine) || 'Motor belirtilmemiş',
            }))
        : [];
      apiSource = String(payload.source || '');
      partGroups = Array.isArray(payload.partGroups) ? payload.partGroups : [];

      fillSelect(brand, 'Marka seç', uniqueSorted(vehicles.map((v) => v.brand)));
      fillSelect(model, 'Model seç', []);
      fillSelect(year, 'Yıl seç', []);
      fillSelect(engine, 'Motor seç', []);
      fillSelect(partGroup, 'Parça grubu seç', []);

      if (vehicles.length) {
        const brandCount = uniqueSorted(vehicles.map((v) => v.brand)).length;
        setStatus(
          `${brandCount} marka ve ${vehicles.length} araç seçeneği Engine'den yüklendi.`,
          'success'
        );
      } else {
        setStatus('Araç Master verisi bulunamadı. OEM / parça koduyla arama yapabilirsiniz.', 'error');
      }
    } catch (error) {
      console.error('YedekPar araç seçici:', error);
      fillSelect(brand, 'Marka seç', []);
      fillSelect(model, 'Model seç', []);
      fillSelect(year, 'Yıl seç', []);
      fillSelect(engine, 'Motor seç', []);
      fillSelect(partGroup, 'Parça grubu seç', []);
      setStatus('Araç verileri alınamadı. Lütfen tekrar deneyin veya OEM koduyla arama yapın.', 'error');
    }

    brand.addEventListener('change', () => {
      const brandVehicles = currentBrandVehicles();
      const exactModels = modelOptionsFromVehicles(brandVehicles);

      fillSelect(model, 'Model seç', exactModels);
      fillSelect(year, 'Yıl seç', []);
      fillSelect(engine, 'Motor seç', []);
      fillSelect(partGroup, 'Parça grubu seç', []);

      if (brand.value && exactModels.length) {
        setStatus(`${brand.value} için ${exactModels.length} model bulundu. Modeli seçin.`, 'success');
      } else if (brand.value) {
        setStatus(`${brand.value} için Araç Master kaydı bulunamadı.`, 'error');
      } else {
        setStatus(
          vehicles.length
            ? `${uniqueSorted(vehicles.map((v) => v.brand)).length} marka ve ${vehicles.length} araç seçeneği hazır.`
            : 'Araç seçeneği bulunamadı.',
          vehicles.length ? 'success' : 'error'
        );
      }

      updateButton();
    });

    model.addEventListener('change', () => {
      const matched = currentModelVehicles();
      const years = uniqueSorted(matched.map(yearLabel));

      fillSelect(year, 'Yıl seç', years);
      fillSelect(engine, 'Motor seç', []);
      fillSelect(partGroup, 'Parça grubu seç', []);

      if (model.value && years.length) {
        setStatus(`${brand.value} ${model.selectedOptions?.[0]?.textContent?.trim() || model.value} için ${years.length} yıl/dönem seçeneği bulundu.`, 'success');
      } else if (model.value) {
        setStatus('Bu model için yıl bilgisi bulunamadı.', 'error');
      }

      updateButton();
    });

    year.addEventListener('change', () => {
      const engines = uniqueSorted(
        currentModelVehicles()
          .filter((v) => yearLabel(v) === year.value)
          .map((v) => String(v.engine || '').trim())
      );

      fillSelect(engine, 'Motor seç', engines);

      if (year.value && engines.length) {
        setStatus(`${year.value} için ${engines.length} motor seçeneği bulundu.`, 'success');
      } else if (year.value) {
        setStatus('Bu yıl/dönem için motor bilgisi bulunamadı.', 'error');
      }

      updateButton();
    });

    engine.addEventListener('change', () => {
      fillSelect(partGroup, 'Parça grubu seç', engine.value ? partGroups : []);
      if (engine.value) setStatus('Araç seçimi tamamlandı. Şimdi parça grubunu seçin.', 'success');
      updateButton();
    });

    partGroup.addEventListener('change', () => {
      if (partGroup.value) setStatus('Parça grubu seçildi. Uyumlu parçaları gösterebilirsiniz.', 'success');
      updateButton();
    });

    oem.addEventListener('input', updateButton);

    const requestedBrand = new URLSearchParams(window.location.search).get('yp_brand');
    if (requestedBrand) {
      const matchingOption = [...brand.options].find(
        (option) => normalizeMatch(option.value) === normalizeMatch(requestedBrand)
      );
      if (matchingOption) {
        brand.value = matchingOption.value;
        brand.dispatchEvent(new Event('change'));

        requestAnimationFrame(() => {
          brand.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });

          window.setTimeout(() => {
            model.focus({ preventScroll: true });
            setStatus(
              `${matchingOption.value} seçildi. Şimdi model seçin.`,
              'success',
            );
          }, 350);
        });
      }
    }

    submit.addEventListener('click', () => {
      if (submit.disabled) return;

      const oemValue = oem.value.trim();
      if (oemValue) {
        const params = new URLSearchParams({ q: oemValue, yp_oem: oemValue });
        window.location.href = `/search?${params.toString()}`;
        return;
      }

      const selected = vehicles.find(
        (v) =>
          normalizeMatch(v.brand) === normalizeMatch(brand.value) &&
          String(v.model || '').trim() === model.value &&
          yearLabel(v) === year.value &&
          String(v.engine || '').trim() === engine.value
      );

      if (!selected) {
        setStatus('Marka, model, yıl ve motor seçimini tamamlayın.', 'error');
        return;
      }

      const modelSearchLabel = vehicleModelLabel(selected) || model.value;
      const selectedOptionLabel = model.selectedOptions?.[0]?.textContent?.trim() || modelSearchLabel;
      const label = `${brand.value} • ${selectedOptionLabel} • ${year.value} • ${engine.value}`;
      const params = new URLSearchParams({
        q: modelSearchLabel,
        yp_vehicle_id: selected.id,
        yp_brand: brand.value,
        yp_model: modelSearchLabel,
        yp_year: year.value,
        yp_engine: engine.value,
        yp_part_group: partGroup.value,
        yp_part_group_label: partGroup.selectedOptions[0].textContent.trim(),
        partGroup: partGroup.value,
        yp_label: label,
      });

      if (apiSource) params.set('yp_source', apiSource);
      window.location.href = `/search?${params.toString()}`;
    });

    updateButton();
  };

  const initializeAll = () =>
    document.querySelectorAll('[data-yp-vehicle-finder]').forEach(initialize);

  document.addEventListener('DOMContentLoaded', initializeAll);
  document.addEventListener('shopify:section:load', initializeAll);
})();
